import React from "react";
import { Form, Button } from "react-bootstrap";
import { Close } from "@mui/icons-material";
import { Autocomplete, TextField, Box } from "@mui/material";
import { MecordColors } from "Constants/Color";
import {
  autocompleteDropdownStyles,
} from "Constants/Style";
import { formatIndianAmount } from "helpers/formatIndianAmount";

export interface AccessoryItem {
  temp_id: string;
  accessory_id?: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  category_name: string;
  sub_category_name: string;
  make_name: string;
  model_name: string;
  unit_name: string;
  hsn_code: string;
  quantity: number;
  unit_price: number;
  discount_type: string; // "percentage" | "currency"
  discount_value: number;
  gst_percentage: number;
  cgst_percentage?: number;
  sgst_percentage?: number;
  igst_percentage?: number;
  remark: string;
  gross_value: number;
  discount_amount: number;
  taxable_amount: number;
  gst_value: number;
  total_amount: number;
}

/** Returns true when discount type represents a percentage discount */
export const isPercentageAccessoryDiscount = (
  discountType?: string | null
): boolean => {
  const raw = (discountType || "").toString().trim().toLowerCase();
  return raw === "%" || raw === "percentage" || raw === "percent";
};

/** Normalize API/UI discount type to form values */
export const normalizeAccessoryDiscountType = (
  discountType?: string | null
): "percentage" | "currency" => {
  return isPercentageAccessoryDiscount(discountType) ? "percentage" : "currency";
};

/** Helper: recalculate derived amounts from base fields */
export const recalcAccessory = (
  acc: AccessoryItem,
  isCompositional = false,
  isIntraState = true
): AccessoryItem => {
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const gross = round2(acc.quantity * acc.unit_price);
  let discAmt = 0;
  if (isPercentageAccessoryDiscount(acc.discount_type)) {
    discAmt = round2((gross * acc.discount_value) / 100);
  } else {
    discAmt = round2(Math.min(acc.discount_value, gross));
  }
  const taxable = round2(gross - discAmt);
  const gst_pct = isCompositional ? 0 : acc.gst_percentage;
  const gstVal = round2((taxable * gst_pct) / 100);
  const total = round2(taxable + gstVal);

  let cgst_pct = 0;
  let sgst_pct = 0;
  let igst_pct = 0;

  if (!isCompositional) {
    if (isIntraState) {
      cgst_pct = round2(gst_pct / 2);
      sgst_pct = round2(gst_pct / 2);
      igst_pct = 0;
    } else {
      cgst_pct = 0;
      sgst_pct = 0;
      igst_pct = gst_pct;
    }
  }

  return {
    ...acc,
    gross_value: gross,
    discount_amount: discAmt,
    taxable_amount: taxable,
    gst_value: gstVal,
    total_amount: total,
    cgst_percentage: cgst_pct,
    sgst_percentage: sgst_pct,
    igst_percentage: igst_pct,
  };
};

/** Normalize product master row for accessory selection */
export const normalizeAccessoryProduct = (product: any) => {
  const gstFromHsn =
    product?.hsn?.gst_percentage ??
    product?.hsn_gst_percentage ??
    product?.gst_percentage ??
    18;

  return {
    product_id: product.product_id,
    product_name: product.product_name || "",
    product_sku: product.product_sku || "",
    category_name:
      product.category_name || product.category?.category_name || "",
    sub_category_name:
      product.sub_category_name ||
      product.sub_category?.sub_category_name ||
      "",
    make_name: product.make_name || product.make?.make_name || "",
    model_name: product.model_name || product.model?.model_name || "",
    unit_name: product.unit_name || product.unit?.unit_name || "Nos",
    hsn_code: product.hsn_code || product.hsn?.hsn_code || "",
    unit_price: Number(product.unit_price || 0),
    gst_percentage: Number(gstFromHsn || 0),
  };
};

/** Create a blank AccessoryItem from product selection */
export const createAccessoryFromProduct = (
  product: any,
  isCompositional = false,
  isIntraState = true
): AccessoryItem => {
  const normalized = normalizeAccessoryProduct(product);
  return recalcAccessory({
    temp_id: `acc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    product_id: normalized.product_id,
    product_name: normalized.product_name,
    product_sku: normalized.product_sku,
    category_name: normalized.category_name,
    sub_category_name: normalized.sub_category_name,
    make_name: normalized.make_name,
    model_name: normalized.model_name,
    unit_name: normalized.unit_name,
    hsn_code: normalized.hsn_code,
    quantity: 1,
    unit_price: normalized.unit_price,
    discount_type: "currency",
    discount_value: 0,
    gst_percentage: normalized.gst_percentage,
    remark: "",
    gross_value: 0,
    discount_amount: 0,
    taxable_amount: 0,
    gst_value: 0,
    total_amount: 0,
  }, isCompositional, isIntraState);
};

/** Map UI accessory → API payload for create/edit */
export const mapAccessoryToApiPayload = (acc: AccessoryItem) => {
  const isPercentage = isPercentageAccessoryDiscount(acc.discount_type);

  return {
    ...(acc.accessory_id ? { accessory_id: acc.accessory_id } : {}),
    product_id: acc.product_id,
    quantity: Number(acc.quantity || 0),
    unit_price: Number(acc.unit_price || 0),
    discount_type: isPercentage ? "%" : "Flat",
    discount_value: Number(acc.discount_value || 0),
    gst_percentage: Number(acc.gst_percentage || 0),
    cgst_percentage: Number(acc.cgst_percentage || 0),
    sgst_percentage: Number(acc.sgst_percentage || 0),
    igst_percentage: Number(acc.igst_percentage || 0),
    remark: acc.remark || null,
  };
};

/** Map API accessory → UI AccessoryItem (edit/view load) */
export const mapApiAccessoryToItem = (
  acc: any,
  isCompositional = false,
  isIntraState = true
): AccessoryItem => {
  const isPercentage = isPercentageAccessoryDiscount(acc.discount_type);

  return recalcAccessory({
    temp_id: acc.accessory_id || `acc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    accessory_id: acc.accessory_id,
    product_id: acc.product_id,
    product_name: acc.product_name || "",
    product_sku: acc.product_sku || "",
    category_name: acc.category_name || "",
    sub_category_name: acc.sub_category_name || "",
    make_name: acc.make_name || "",
    model_name: acc.model_name || "",
    unit_name: acc.unit_name || "Nos",
    hsn_code: acc.hsn_code || "",
    quantity: Number(acc.quantity || 1),
    unit_price: Number(acc.unit_price || 0),
    discount_type: isPercentage ? "percentage" : "currency",
    discount_value: Number(acc.discount_value || 0),
    gst_percentage: Number(acc.gst_percentage || 0),
    remark: acc.remark || "",
    gross_value: Number(acc.gross_value || 0),
    discount_amount: Number(acc.discount_amount || 0),
    taxable_amount: Number(acc.taxable_amount || 0),
    gst_value: Number(acc.gst_value || 0),
    total_amount: Number(acc.total_amount || 0),
  }, isCompositional, isIntraState);
};

interface ServiceAccessoryRowProps {
  accessory: AccessoryItem;
  index: number;
  gstOptions: any[];
  onChange: (updated: AccessoryItem) => void;
  onRemove: () => void;
  errors?: Record<string, string>;
  currencySymbol?: string;
  isCompositional?: boolean;
  isIntraState?: boolean;
}

const commonInputStyle: React.CSSProperties = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

const ServiceAccessoryRow: React.FC<ServiceAccessoryRowProps> = ({
  accessory,
  index,
  gstOptions,
  onChange,
  onRemove,
  errors = {},
  currencySymbol = "₹",
  isCompositional = false,
  isIntraState = true,
}) => {
  const handleFieldChange = (field: string, value: any) => {
    const updated = { ...accessory, [field]: value };
    onChange(recalcAccessory(updated, isCompositional, isIntraState));
  };

  return (
    <div
      className="p-3 mb-3 rounded"
      style={{
        backgroundColor: MecordColors.ActiveBackground || "#FFF5F5",
        border: `1px solid ${MecordColors.SecondaryButtonBorderColor || "#AB2123"}`,
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <span
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: MecordColors.ThemeBGColor || "#AB2123",
            }}
          >
            {index + 1}. {accessory.product_name}
          </span>
          {accessory.product_sku && (
            <span
              className="ms-3 badge rounded-pill"
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "10px",
                backgroundColor: "#fff",
                color: MecordColors.ThemeBGColor || "#AB2123",
                border: `1px solid ${MecordColors.SecondaryButtonBorderColor || "#AB2123"}`,
                fontWeight: 500,
              }}
            >
              SKU: {accessory.product_sku}
            </span>
          )}
          {accessory.hsn_code && (
            <span
              className="ms-2 badge rounded-pill"
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "10px",
                backgroundColor: "#fff",
                color: MecordColors.DefaultText || "#878899",
                border: "1px solid #ced4da",
                fontWeight: 500,
              }}
            >
              HSN: {accessory.hsn_code}
            </span>
          )}
        </div>
        <Button
          variant="link"
          className="text-danger p-0"
          onClick={onRemove}
          title="Remove accessory"
          style={{ lineHeight: 1, textDecoration: "none" }}
        >
          <Close style={{ fontSize: "18px", color: MecordColors.ThemeBGColor || "#AB2123" }} />
        </Button>
      </div>

      <div className="row g-2">
        <div className="col-6 col-md-2">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
              Qty <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={accessory.quantity}
              onChange={(e) =>
                handleFieldChange("quantity", parseInt(e.target.value) || 0)
              }
              size="sm"
              style={{ ...commonInputStyle, minHeight: "31px", height: "31px", borderRadius: "4px" }}
              isInvalid={!!errors.quantity}
            />
            {errors.quantity && (
              <small className="text-danger" style={{ fontSize: "10px" }}>{errors.quantity}</small>
            )}
          </Form.Group>
        </div>

        <div className="col-6 col-md-2">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
              Unit
            </Form.Label>
            <Form.Control
              type="text"
              value={accessory.unit_name}
              onChange={(e) => handleFieldChange("unit_name", e.target.value)}
              size="sm"
              style={{
                ...commonInputStyle,
                minHeight: "31px",
                height: "31px",
                backgroundColor: "#e9ecef",
                borderRadius: "4px",
              }}
              readOnly
            />
          </Form.Group>
        </div>

        <div className="col-6 col-md-2">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
              Rate ({currencySymbol}) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={accessory.unit_price}
              onChange={(e) =>
                handleFieldChange("unit_price", parseFloat(e.target.value) || 0)
              }
              size="sm"
              style={{ ...commonInputStyle, minHeight: "31px", height: "31px", borderRadius: "4px" }}
              isInvalid={!!errors.unit_price}
            />
            {errors.unit_price && (
              <small className="text-danger" style={{ fontSize: "10px" }}>{errors.unit_price}</small>
            )}
          </Form.Group>
        </div>

        <div className="col-6 col-md-3">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
              Discount
            </Form.Label>
            <div className="d-flex gap-1">
              <Form.Control
                type="number"
                min="0"
                max={
                  isPercentageAccessoryDiscount(accessory.discount_type)
                    ? 100
                    : accessory.gross_value
                }
                step="0.01"
                value={accessory.discount_value}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  if (
                    isPercentageAccessoryDiscount(accessory.discount_type) &&
                    val > 100
                  )
                    return;
                  if (
                    !isPercentageAccessoryDiscount(accessory.discount_type) &&
                    val > accessory.gross_value
                  )
                    return;
                  handleFieldChange("discount_value", val);
                }}
                size="sm"
                style={{
                  ...commonInputStyle,
                  minHeight: "31px",
                  height: "31px",
                  borderRadius: "4px",
                  flex: 1,
                }}
              />
              <Form.Select
                size="sm"
                value={normalizeAccessoryDiscountType(accessory.discount_type)}
                onChange={(e) => {
                  handleFieldChange("discount_value", 0);
                  handleFieldChange("discount_type", e.target.value);
                }}
                style={{
                  ...commonInputStyle,
                  minHeight: "31px",
                  height: "31px",
                  width: "55px",
                  padding: "2px 4px",
                  borderRadius: "4px",
                }}
              >
                <option value="currency">{currencySymbol}</option>
                <option value="percentage">%</option>
              </Form.Select>
            </div>
          </Form.Group>
        </div>

        <div className="col-6 col-md-3">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
              GST (%)
            </Form.Label>
            <Autocomplete
              disabled={isCompositional}
              disableClearable
              options={gstOptions}
              getOptionLabel={(option: any) =>
                option.gst_percentage?.toString() || ""
              }
              isOptionEqualToValue={(option: any, value: any) =>
                option.gst_percentage === value.gst_percentage
              }
              value={
                gstOptions.find(
                  (o: any) =>
                    o.gst_percentage?.toString() ===
                    accessory.gst_percentage?.toString()
                ) || null
              }
              onChange={(_, newValue) => {
                handleFieldChange(
                  "gst_percentage",
                  parseFloat(newValue?.gst_percentage || "0")
                );
              }}
              size="small"
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option: any, { selected }) => (
                <li
                  {...props}
                  key={option.gst_id || option.gst_percentage}
                  style={{ padding: 0 }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      width: "100%",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover.backgroundColor,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    {option.gst_percentage}%
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="GST %"
                  size="small"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      minHeight: "31px",
                      height: "31px",
                      fontSize: "0.875rem",
                      padding: "0 8px",
                      backgroundColor: "#fff",
                    },
                    "& .MuiInputBase-input": {
                      padding: "4px 0",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              )}
            />
          </Form.Group>
        </div>

        {isIntraState ? (
          <>
            <div className="col-6 col-md-3">
              <Form.Group>
                <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
                  SGST (%)
                </Form.Label>
                <Form.Control
                  type="text"
                  value={accessory.sgst_percentage ?? 0}
                  disabled
                  size="sm"
                  style={{ ...commonInputStyle, minHeight: "31px", height: "31px", borderRadius: "4px", backgroundColor: "#f8f9fa" }}
                />
              </Form.Group>
            </div>
            <div className="col-6 col-md-3">
              <Form.Group>
                <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
                  CGST (%)
                </Form.Label>
                <Form.Control
                  type="text"
                  value={accessory.cgst_percentage ?? 0}
                  disabled
                  size="sm"
                  style={{ ...commonInputStyle, minHeight: "31px", height: "31px", borderRadius: "4px", backgroundColor: "#f8f9fa" }}
                />
              </Form.Group>
            </div>
          </>
        ) : (
          <div className="col-6 col-md-3">
            <Form.Group>
              <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
                IGST (%)
              </Form.Label>
              <Form.Control
                type="text"
                value={accessory.igst_percentage ?? 0}
                disabled
                size="sm"
                style={{ ...commonInputStyle, minHeight: "31px", height: "31px", borderRadius: "4px", backgroundColor: "#f8f9fa" }}
              />
            </Form.Group>
          </div>
        )}
      </div>

      <div className="row g-2 mt-2 pt-2 border-top" style={{ borderColor: "rgba(171, 33, 35, 0.15)" }}>
        <div className="col-6 col-md-2">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "10px", marginBottom: 0, color: MecordColors.DefaultText }}>
              Gross Value
            </Form.Label>
            <div style={{ ...commonInputStyle, fontSize: "12px", fontWeight: 500, color: "#495057" }}>
              {currencySymbol} {formatIndianAmount(accessory.gross_value)}
            </div>
          </Form.Group>
        </div>
        <div className="col-6 col-md-2">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "10px", marginBottom: 0, color: MecordColors.DefaultText }}>
              Taxable
            </Form.Label>
            <div style={{ ...commonInputStyle, fontSize: "12px", fontWeight: 500, color: "#495057" }}>
              {currencySymbol} {formatIndianAmount(accessory.taxable_amount)}
            </div>
          </Form.Group>
        </div>
        <div className="col-6 col-md-2">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "10px", marginBottom: 0, color: MecordColors.DefaultText }}>
              GST Value
            </Form.Label>
            <div style={{ ...commonInputStyle, fontSize: "12px", fontWeight: 500, color: "#495057" }}>
              {currencySymbol} {formatIndianAmount(accessory.gst_value)}
            </div>
          </Form.Group>
        </div>
        <div className="col-6 col-md-2">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "10px", marginBottom: 0, color: MecordColors.DefaultText }}>
              Total
            </Form.Label>
            <div style={{ ...commonInputStyle, fontSize: "13px", fontWeight: 600, color: MecordColors.ThemeBGColor || "#AB2123" }}>
              {currencySymbol} {formatIndianAmount(accessory.total_amount)}
            </div>
          </Form.Group>
        </div>
        <div className="col-12 col-md-4">
          <Form.Group>
            <Form.Label style={{ ...commonInputStyle, fontSize: "11px", fontWeight: 500, marginBottom: 2 }}>
              Remark
            </Form.Label>
            <Form.Control
              type="text"
              value={accessory.remark}
              onChange={(e) => handleFieldChange("remark", e.target.value)}
              placeholder="Enter remark..."
              size="sm"
              style={{ ...commonInputStyle, minHeight: "31px", height: "31px", borderRadius: "4px" }}
            />
          </Form.Group>
        </div>
      </div>
    </div>
  );
};

export default ServiceAccessoryRow;
